import { RootState } from '../store'
import { createSelector } from '@reduxjs/toolkit'
import { ActiveLayoutItem } from './initialState'

const selectStudioState = (state: RootState) => state.studio.present

export const selectActivePageId = createSelector(
    [selectStudioState],
    (studio) => studio.activePageId,
)

export const selectCoverImage = createSelector(
    [selectStudioState],
    (studio) => studio.coverImage || null,
)

export const selectCoverImageId = createSelector(
    [selectCoverImage],
    (coverImage) => coverImage?.id,
)

const selectPages = (state: RootState) => state.studio.present.pages

export const selectActivePageData = createSelector(
    [selectPages, selectActivePageId],
    (pages, activePageId) => pages[activePageId] || null,
)

export const selectAssets = createSelector(
    [selectActivePageData],
    (activePage) => activePage?.assets || {},
)

export const selectLayouts = createSelector(
    [selectActivePageData],
    (activePage) => activePage?.layouts,
)

export const selectResponsiveMode = createSelector(
    [selectStudioState],
    (studio) => studio.responsiveMode,
)

export const selectSelectedItemIds = createSelector(
    [selectActivePageData],
    (activePage) => activePage?.selectedItemIds || [],
)

export const selectActiveLayoutItems = createSelector(
    [selectAssets, selectLayouts, selectResponsiveMode],
    (assets, layouts, mode): ActiveLayoutItem[] => {
        try {
            const activeLayout = layouts?.[mode]
            if (!activeLayout) return []

            return activeLayout.itemIds
                .map((id): ActiveLayoutItem | null => {
                    const layoutItem = activeLayout.items[id]
                    if (!layoutItem) return null
                    const assetItem = assets[layoutItem.assetId]
                    if (!assetItem) return null

                    const activeItem: ActiveLayoutItem = {
                        ...layoutItem,
                        type: assetItem.type,
                        content: assetItem.content,
                        itemId: assetItem.itemId,
                        collectionSlug: assetItem.collectionSlug,
                        contentType: assetItem.contentType,
                    }

                    return activeItem
                })
                .filter((item): item is ActiveLayoutItem => item !== null)
        } catch (e: any) {
            console.error(e)
            return []
        }
    },
)

export const selectSingleSelectedItem = createSelector(
    [selectActiveLayoutItems, selectSelectedItemIds],
    (items, selectedIds): ActiveLayoutItem | null => {
        if (selectedIds.length !== 1) {
            return null
        }
        return items.find((i) => i.id === selectedIds[0]) || null
    },
)

export const selectGroupSelectedItems = createSelector(
    [selectActiveLayoutItems, selectSelectedItemIds],
    (items, selectedIds): ActiveLayoutItem[] => {
        if (selectedIds.length < 2) {
            return []
        }
        return items.filter((i) => selectedIds.includes(i.id))
    },
)

// ---------------------

export const selectIsGridVisible = createSelector(
    [selectStudioState],
    (studio) => studio.isGridVisible,
)

export const selectAllCollectionSelections = createSelector(
    [selectStudioState],
    (studio) => studio.selectedItemsByCollection,
)
